import React, { useEffect, useState } from "react";
import { Edit3, Key, Plus, ShieldCheck, ShieldOff, Trash2, X } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const ALL_PERMISSIONS = [
  { key: "dashboard",     label: "Dashboard" },
  { key: "users",         label: "Users" },
  { key: "deposits",      label: "Deposits" },
  { key: "withdraws",     label: "Payouts / Withdrawals" },
  { key: "results",       label: "Results & Starline" },
  { key: "markets",       label: "Game Management" },
  { key: "stake_analysis",label: "Stake Analysis" },
  { key: "banners",       label: "Banners" },
  { key: "promotions",    label: "Promotions & Bonus" },
  { key: "notifications", label: "Send Notifications" },
  { key: "charts",        label: "Charts" },
  { key: "agents",        label: "Agent Program" },
  { key: "settings",      label: "Settings & Payment" },
  { key: "sub_admins",    label: "Sub Admins (this page)" },
  { key: "resets",        label: "Record Resets" },
];

const emptyForm = () => ({
  name: "", email: "", password: "", is_active: true,
  permissions: Object.fromEntries(ALL_PERMISSIONS.map((p) => [p.key, false])),
});

function AdminSubAdmins() {
  const [list, setList]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId]   = useState(null);
  const [form, setForm]       = useState(emptyForm());
  const [saving, setSaving]   = useState(false);

  // permissions-only modal
  const [permModal, setPermModal] = useState(null); // { id, name, permissions }
  const [permSaving, setPermSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await API.get("/admin/sub-admins");
      setList(res.data.sub_admins || []);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to load sub admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (sa) => {
    setEditId(sa.id);
    setForm({
      name: sa.name || "",
      email: sa.email || "",
      password: "",
      is_active: !!sa.is_active,
      permissions: { ...Object.fromEntries(ALL_PERMISSIONS.map((p) => [p.key, false])), ...(sa.permissions || {}) },
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      alert("Name and email are required");
      return;
    }
    if (!editId && form.password.length < 4) {
      alert("Password must be at least 4 characters");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        is_active: form.is_active,
        permissions: form.permissions,
      };
      if (editId) {
        await API.put(`/admin/sub-admins/${editId}`, payload);
      } else {
        await API.post("/admin/sub-admins", payload);
      }
      await load();
      setShowModal(false);
    } catch (err) {
      alert(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePerm = (key) => {
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  };

  const selectAll = () => {
    setForm((f) => ({ ...f, permissions: Object.fromEntries(ALL_PERMISSIONS.map((p) => [p.key, true])) }));
  };
  const clearAll = () => {
    setForm((f) => ({ ...f, permissions: Object.fromEntries(ALL_PERMISSIONS.map((p) => [p.key, false])) }));
  };

  const openPermModal = (sa) => {
    setPermModal({
      id: sa.id,
      name: sa.name,
      permissions: { ...Object.fromEntries(ALL_PERMISSIONS.map((p) => [p.key, false])), ...(sa.permissions || {}) },
    });
  };

  const togglePermModal = (key) => {
    setPermModal((m) => ({ ...m, permissions: { ...m.permissions, [key]: !m.permissions[key] } }));
  };

  const savePermissions = async () => {
    setPermSaving(true);
    try {
      await API.patch(`/admin/sub-admins/${permModal.id}/permissions`, { permissions: permModal.permissions });
      await load();
      setPermModal(null);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to save permissions");
    } finally {
      setPermSaving(false);
    }
  };

  const deleteSubAdmin = async (sa) => {
    if (!window.confirm(`Delete sub admin "${sa.name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/admin/sub-admins/${sa.id}`);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete sub admin");
    }
  };

  return (
    <AdminLayout>
      <div style={wrap}>
        <div style={topRow}>
          <div>
            <h1 style={title}>Sub Admins</h1>
            <p style={subtitle}>Create sub admins and control which pages they can access</p>
          </div>
          <button type="button" style={addBtn} onClick={openCreate}>
            <Plus size={16} /> Create Sub Admin
          </button>
        </div>

        {loading ? (
          <div style={loadMsg}>Loading...</div>
        ) : list.length === 0 ? (
          <div style={emptyBox}>No sub admins yet. Click "Create Sub Admin" to add one.</div>
        ) : (
          <div style={tableWrap}>
            <table style={tbl}>
              <thead>
                <tr>
                  {["#", "Name", "Email", "Status", "Created By", "Permissions", "Actions"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((sa) => {
                  const activePerms = ALL_PERMISSIONS.filter((p) => sa.permissions?.[p.key]);
                  return (
                    <tr key={sa.id} style={tr}>
                      <td style={td}>{sa.id}</td>
                      <td style={td}><strong>{sa.name}</strong></td>
                      <td style={td}>{sa.email}</td>
                      <td style={td}>
                        <span style={sa.is_active ? activeBadge : inactiveBadge}>
                          {sa.is_active ? <><ShieldCheck size={12}/> Active</> : <><ShieldOff size={12}/> Disabled</>}
                        </span>
                      </td>
                      <td style={td}>{sa.created_by_name || "—"}</td>
                      <td style={{ ...td, maxWidth: 260 }}>
                        <div style={permWrap}>
                          {activePerms.length === 0
                            ? <span style={noPermText}>No permissions</span>
                            : activePerms.map((p) => (
                                <span key={p.key} style={permChip}>{p.label}</span>
                              ))
                          }
                        </div>
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button type="button" style={editBtn} onClick={() => openEdit(sa)}>
                            <Edit3 size={13}/> Edit
                          </button>
                          <button type="button" style={permBtn} onClick={() => openPermModal(sa)}>
                            <Key size={13}/> Permissions
                          </button>
                          <button type="button" style={delBtn} onClick={() => deleteSubAdmin(sa)}>
                            <Trash2 size={13}/> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div style={overlay}>
          <div style={modal}>
            <div style={modalHead}>
              <span style={modalTitle}>{editId ? "Edit Sub Admin" : "Create Sub Admin"}</span>
              <button type="button" style={closeBtn} onClick={() => setShowModal(false)}>
                <X size={18}/>
              </button>
            </div>

            <div style={modalBody}>
                <div style={fieldRow}>
                  <div style={fieldGroup}>
                    <label style={lbl}>Full Name *</label>
                    <input style={inp} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Rahul Sharma" />
                  </div>
                  <div style={fieldGroup}>
                    <label style={lbl}>Email *</label>
                    <input style={inp} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="email@example.com" />
                  </div>
                </div>

                <div style={fieldRow}>
                  <div style={fieldGroup}>
                    <label style={lbl}>Password {editId ? "(leave blank to keep current)" : "*"}</label>
                    <input style={inp} type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Min 4 characters" />
                  </div>
                  <div style={fieldGroup}>
                    <label style={lbl}>Status</label>
                    <select style={inp} value={form.is_active ? "1" : "0"} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === "1" }))}>
                      <option value="1">Active</option>
                      <option value="0">Disabled</option>
                    </select>
                  </div>
                </div>

                <div style={permSection}>
                  <div style={permHeader}>
                    <span style={permTitle}>Page Permissions</span>
                    <div style={permActions}>
                      <button type="button" style={selAllBtn} onClick={selectAll}>Select All</button>
                      <button type="button" style={selAllBtn} onClick={clearAll}>Clear All</button>
                    </div>
                  </div>
                  <div style={permGrid}>
                    {ALL_PERMISSIONS.map((p) => (
                      <label key={p.key} style={permLabel}>
                        <input
                          type="checkbox"
                          checked={!!form.permissions[p.key]}
                          onChange={() => togglePerm(p.key)}
                          style={chk}
                        />
                        <span style={form.permissions[p.key] ? permLabelTextOn : permLabelText}>{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={modalFoot}>
                  <button type="button" style={cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="button" style={saving ? saveBtnDisabled : saveBtn} onClick={save} disabled={saving}>
                    {saving ? "Saving..." : editId ? "Save Changes" : "Create Sub Admin"}
                  </button>
                </div>
              </div>
          </div>
        </div>
      )}
      {permModal && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 480 }}>
            <div style={modalHead}>
              <div>
                <span style={modalTitle}>Edit Permissions</span>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{permModal.name}</div>
              </div>
              <button type="button" style={closeBtn} onClick={() => setPermModal(null)}><X size={18}/></button>
            </div>
            <div style={modalBody}>
              <div style={permSection}>
                <div style={permHeader}>
                  <span style={permTitle}>Page Access</span>
                  <div style={permActions}>
                    <button type="button" style={selAllBtn} onClick={() => setPermModal((m) => ({ ...m, permissions: Object.fromEntries(ALL_PERMISSIONS.map((p) => [p.key, true])) }))}>All</button>
                    <button type="button" style={selAllBtn} onClick={() => setPermModal((m) => ({ ...m, permissions: Object.fromEntries(ALL_PERMISSIONS.map((p) => [p.key, false])) }))}>None</button>
                  </div>
                </div>
                <div style={permGrid}>
                  {ALL_PERMISSIONS.map((p) => (
                    <label key={p.key} style={permLabel}>
                      <input
                        type="checkbox"
                        checked={!!permModal.permissions[p.key]}
                        onChange={() => togglePermModal(p.key)}
                        style={chk}
                      />
                      <span style={permModal.permissions[p.key] ? permLabelTextOn : permLabelText}>{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div style={modalFoot}>
              <button type="button" style={cancelBtn} onClick={() => setPermModal(null)}>Cancel</button>
              <button type="button" style={permSaving ? saveBtnDisabled : saveBtn} onClick={savePermissions} disabled={permSaving}>
                {permSaving ? "Saving..." : "Save Permissions"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const wrap      = { padding: "24px 20px", maxWidth: 1100, margin: "0 auto" };
const topRow    = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, gap: 12, flexWrap: "wrap" };
const title     = { margin: 0, fontSize: 22, fontWeight: 900, color: "#0f172a" };
const subtitle  = { margin: "4px 0 0", fontSize: 13, color: "#64748b" };
const addBtn    = { display: "flex", alignItems: "center", gap: 6, height: 38, padding: "0 16px", background: "#f97316", border: 0, borderRadius: 10, color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" };
const loadMsg   = { padding: 40, textAlign: "center", color: "#64748b" };
const emptyBox  = { padding: 40, textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 12, border: "1px dashed #e2e8f0" };

const tableWrap = { overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0", background: "#fff" };
const tbl       = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const th        = { padding: "10px 14px", textAlign: "left", fontWeight: 800, color: "#475569", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" };
const tr        = { borderBottom: "1px solid #f1f5f9" };
const td        = { padding: "10px 14px", color: "#0f172a", verticalAlign: "top" };

const activeBadge   = { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 999, background: "#dcfce7", color: "#16a34a", fontSize: 11, fontWeight: 700 };
const inactiveBadge = { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 999, background: "#f1f5f9", color: "#64748b", fontSize: 11, fontWeight: 700 };
const permWrap    = { display: "flex", flexWrap: "wrap", gap: 4 };
const noPermText  = { color: "#94a3b8", fontSize: 11 };
const permChip    = { background: "#eff6ff", color: "#1d4ed8", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap" };
const editBtn     = { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#374151", cursor: "pointer" };
const permBtn     = { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#1d4ed8", cursor: "pointer" };
const delBtn      = { display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#e11d48", cursor: "pointer" };

const overlay   = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const modal     = { background: "#fff", borderRadius: 18, width: "100%", maxWidth: 680, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.2)" };
const modalHead = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #e2e8f0" };
const modalTitle= { fontSize: 17, fontWeight: 900, color: "#0f172a" };
const closeBtn  = { border: 0, background: "transparent", cursor: "pointer", color: "#64748b", display: "grid", placeItems: "center", padding: 4 };
const modalBody = { padding: "20px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 };
const modalFoot = { display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 20px", borderTop: "1px solid #e2e8f0" };

const fieldRow   = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };
const fieldGroup = { display: "flex", flexDirection: "column", gap: 6 };
const lbl        = { fontSize: 12, fontWeight: 700, color: "#374151" };
const inp        = { height: 38, border: "1px solid #e2e8f0", borderRadius: 8, padding: "0 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };

const permSection = { border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" };
const permHeader  = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" };
const permTitle   = { fontSize: 13, fontWeight: 800, color: "#0f172a" };
const permActions = { display: "flex", gap: 8 };
const selAllBtn   = { border: "1px solid #e2e8f0", background: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", color: "#374151" };
const permGrid    = { padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 };
const permLabel   = { display: "flex", alignItems: "center", gap: 8, cursor: "pointer" };
const chk         = { width: 15, height: 15, cursor: "pointer", accentColor: "#f97316" };
const permLabelText   = { fontSize: 12, color: "#64748b", fontWeight: 500 };
const permLabelTextOn = { fontSize: 12, color: "#0f172a", fontWeight: 700 };

const cancelBtn = { height: 38, padding: "0 18px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", fontWeight: 700, fontSize: 13, cursor: "pointer" };
const saveBtn   = { height: 38, padding: "0 20px", border: 0, borderRadius: 8, background: "#f97316", color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer" };
const saveBtnDisabled = { ...saveBtn, background: "#e2e8f0", color: "#94a3b8", cursor: "not-allowed" };


export default AdminSubAdmins;
